import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { PushDevice } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';

import { useRemovePushDevice } from '../hooks/use-remove-push-device';
import { describeUserAgent } from '../lib/device-label';

function useDeviceName() {
  const { t } = useTranslation('push');
  return (device: PushDevice) => {
    const { browser, os } = describeUserAgent(device.userAgent);
    if (browser && os) return t('devices.label', { browser, os });
    return browser ?? os ?? t('devices.unknown');
  };
}

export interface DevicesCardProps {
  devices: PushDevice[];
  thisDeviceId: string | null;
}

/** Every browser the member turned push on in; this one is switched off from its own card. */
export function DevicesCard({ devices, thisDeviceId }: DevicesCardProps) {
  const { t } = useTranslation('push');
  const format = useFormat();
  const name = useDeviceName();
  const remove = useRemovePushDevice();

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('devices.title')}</CardTitle>
        <CardDescription>{t('devices.description')}</CardDescription>
      </CardHeader>
      <CardContent className="text-sm">
        {devices.length === 0 ? (
          <p className="text-muted-foreground">{t('devices.empty')}</p>
        ) : (
          <ul className="divide-border flex flex-col divide-y" data-testid="push-devices">
            {devices.map((device) => {
              const label = name(device);
              const mine = device.id === thisDeviceId;
              return (
                <li key={device.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex flex-wrap items-center gap-2 font-medium">
                      {label}
                      {mine ? <Badge variant="secondary">{t('devices.thisDevice')}</Badge> : null}
                    </span>
                    <span className="text-muted-foreground truncate text-xs">
                      {device.lastSuccessAt
                        ? t('devices.lastDelivered', {
                            date: format.dateTime(device.lastSuccessAt),
                          })
                        : t('devices.added', { date: format.dateTime(device.createdAt) })}
                      {` · ${device.host}`}
                    </span>
                  </div>
                  {mine ? null : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => remove.mutate(device.id)}
                      disabled={remove.isPending}
                      aria-label={t('devices.removeLabel', { name: label })}
                    >
                      <Trash2 aria-hidden />
                      {t('devices.remove')}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
